import { icons, LucideProps } from 'lucide-react'

type Props = LucideProps & { name: string }

// Legacy KTIcon names mapped to semantically appropriate Lucide icons.
// Each choice reflects what the route/feature actually represents.
// Names verified against installed lucide-react v1.14.0 export list.
const KEEN_TO_LUCIDE: Record<string, string> = {
  // ── Sidebar / featureConfig routes ───────────────────────────────────────
  'color-swatch': 'LayoutDashboard',  // org dashboard / overview
  'element-plus': 'Gauge',            // main dashboard
  'fingerprint-scanning': 'ScanLine',         // scan menu
  'questionnaire-tablet': 'History',          // scan history / report history
  'watch': 'CalendarClock',    // scan schedule
  'plus-square': 'CirclePlus',       // create scan
  'wrench': 'Wrench',           // toolbox menu
  'magnifier': 'Search',           // dns lookup tool
  'sms': 'Mail',             // smtp / email
  'lock-3': 'Lock',             // ssl tool
  'technology-4': 'Radar',            // attack surface
  'geolocation': 'Globe',            // domains
  'router': 'Server',           // ip addresses
  'route': 'Network',          // ports
  'category': 'FileCode',         // dns records
  'shield-tick': 'ShieldCheck',      // ssl certificates
  'laptop': 'AppWindow',        // applications
  'technology-2': 'Cpu',              // technologies
  'security-check': 'DatabaseZap',      // leaked data
  'shield-slash': 'ShieldAlert',      // vulnerabilities
  'data': 'Workflow',         // workflow
  'gear': 'Plug',             // integrations
  'some-files': 'FileText',         // reports menu
  'add-notepad': 'FilePlus',         // create report
  'devices-2': 'LayoutTemplate',   // report templates
  'illustrator': 'Bot',              // chat bot
  // ── Workflow / drawer / schedule modal ───────────────────────────────────
  'time': 'Clock',
  'cd': 'Disc',
  'arrows-circle': 'RefreshCw',
  'arrow-down': 'ArrowDown',
  'arrow-up': 'ArrowUp',
  'up': 'ChevronUp',
  'down': 'ChevronDown',
  'element-11': 'LayoutGrid',
  'exit-right-corner': 'LogOut',
  'app-header-menu': 'Menu',
  'app-header-minimize': 'PanelLeftClose',
  // ── Common KTIcon aliases (status, actions, generic) ─────────────────────
  'notification-on': 'BellRing',
  'notification': 'Bell',
  'profile-circle': 'UserRound',
  'profile-user': 'User',
  'user': 'User',
  'users': 'Users',
  'home': 'House',
  'home-2': 'House',
  'home-3': 'House',
  'check': 'Check',
  'check-circle': 'CircleCheck',
  'cross': 'Cross',
  'cross-circle': 'CircleX',
  'plus': 'Plus',
  'minus': 'Minus',
  'trash': 'Trash2',
  'pencil': 'Pencil',
  'eye': 'Eye',
  'eye-slash': 'EyeOff',
  'setting-2': 'Settings',
  'setting-3': 'Settings',
  'setting-4': 'Settings',
  'document': 'FileText',
  'file': 'File',
  'files': 'Files',
  'folder': 'Folder',
  'calendar': 'Calendar',
  'mail': 'Mail',
  'phone': 'Phone',
  'globe': 'Globe',
  'shield': 'Shield',
  'key': 'Key',
  'lock': 'Lock',
  'unlock': 'LockOpen',
  'star': 'Star',
  'heart': 'Heart',
  'bookmark': 'Bookmark',
  'flag': 'Flag',
  'tag': 'Tag',
  'filter': 'Funnel',
  'sort': 'ArrowUpDown',
  'refresh': 'RefreshCw',
  'reload': 'RefreshCw',
  'download': 'Download',
  'upload': 'Upload',
  'send': 'Send',
  'share': 'Share2',
  'copy': 'Copy',
  'link': 'Link',
  'external-link': 'ExternalLink',
  'arrow-left': 'ArrowLeft',
  'arrow-right': 'ArrowRight',
  'chart': 'ChartBar',
  'chart-bar': 'ChartBar',
  'chart-line': 'ChartLine',
  'chart-pie': 'ChartPie',
  'menu': 'Menu',
  'dots-horizontal': 'Ellipsis',
  'dots-vertical': 'EllipsisVertical',
  'play': 'Play',
  'pause': 'Pause',
  'stop': 'Square',
  'briefcase': 'Briefcase',
  'cloud': 'Cloud',
  'server': 'Server',
  'database': 'Database',
  'bug': 'Bug',
  'flash': 'Zap',
  'rocket': 'Rocket',
  'package': 'Package',
  'box': 'Box',
  'graph': 'ChartBar',
  'graph-up': 'TrendingUp',
  'graph-down': 'TrendingDown',
  'message-text': 'MessageSquare',
  'message-text-2': 'MessageSquare',
  'questionnaire': 'CircleQuestionMark',
  'information': 'Info',
  'information-2': 'Info',
  'information-3': 'Info',
  'information-4': 'Info',
  'information-5': 'Info',
  'abstract-14': 'LayoutTemplate',
  'abstract-26': 'Grid2x2',
  'abstract-39': 'ChartBar',
  'compass': 'Compass',
  'map': 'Map',
  'mailAddress': 'AtSign',

}

const toPascal = (s: string) =>
  s.replace(/(^|[-_])(\w)/g, (_, __, c: string) => c.toUpperCase())

export function DynamicIcon({ name, ...props }: Props) {
  if (!name) return null
  const mapped = KEEN_TO_LUCIDE[name] ?? toPascal(name)
  const Icon = icons[mapped as keyof typeof icons]
  if (!Icon) return null
  return <Icon {...props} />
}

export default DynamicIcon
